import React, { useState, useCallback, useEffect, useMemo } from 'react';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { FormSpy } from 'react-final-form';
import { FormState, FormApi, FORM_ERROR } from 'final-form';
import { CircularProgress } from '@material-ui/core';
import { changeFieldValue } from 'utils/formMutators';
import routePaths from 'routes/routePaths';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { OTPVerificationModal, StatusModal, WarningModal } from 'components/modals';
import { FormType as FormTypeOTP } from 'components/modals/OTPVerificationModal/OTPVerificationModal';
import { IApiResponse } from 'utils/models';
import { FABSORTCODE, NETWORK_REQUIRED } from 'utils/constants';
import { validationCallback, requiredOnly } from 'utils/validation';
import api from 'api/api';
import Loader from 'ui/Loader/Loader';

import { IAccount } from 'store/accounts.model';
import { Address, CartodbResponse } from 'components/modules/BranchLocator/BranchLocator.model';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import { IFormValues } from './OfficialStatement.model';
import renderBankStatementFields from './OfficialStatementFields';

let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;

// const center = {
//   lat: 5.560014,
//   lng: -0.205744,
// };

const getParsedDate = (date) => {
  const d = new Date(date);
  const currDate = d.getDate();
  const currMonth = d.getMonth() + 1;
  const currYear = d.getFullYear();
  return `${currYear}-${currMonth}-${currDate}`;
};

const today = new Date();
today.setHours(23, 59, 59, 59);

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  startDate: yup
    .date()
    .required('Start Date is required')
    .typeError('Start Date is not verified')
    .max(today, 'Please choose past date'),
  endDate: yup
    .date()
    .required('End Date is required')
    .typeError('End Date is not verified')
    .min(yup.ref('startDate'), 'End date should be equal or greater than start date')
    .max(today, 'Please choose past date'),
  branch: requiredOnly('Branch'),
});

interface IProps {
  accounts: IAccount[];
  handleChangeForm: () => void;
}

const OfficialStatement = ({ accounts, handleChangeForm }: IProps) => {
  const [branches, setBranches] = useState<Address[]>([]);
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [loadingData, setLoadingData] = useState(false);
  const [isOTPModalOpen, setOTPModalOpen] = useState(false);
  const [showStatementWarning, setShowStatementWarning] = useState(false);
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');

  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  useEffect(() => {
    setLoadingData(true);

    // New branch locator endpoint
    api
      .get(`transfer/bankBranch?bankSortCode=${FABSORTCODE}`, {}, true, true)

      .then((res: CartodbResponse) => {
        setBranches(res?.banks);
      })

      .catch((error: IApiResponse) => {
        console.error(error);
      })
      .finally(() => setLoadingData(false));
  }, []);

  const initialValues: IFormValues = useMemo(() => {
    const emptyValues = {
      sourceAccount: '',
      startDate: '',
      endDate: '',
      branch: '',
    };

    return emptyValues;
  }, []);

  const handleChangeStartDate = (value: string) => {
    mainForm.change('startDate', value);
  };

  const handleChangeEndDate = (value: string) => {
    mainForm.change('endDate', value);
  };

  const openConfirmation = () => setOTPModalOpen(true);
  const closeConfirmation = () => setOTPModalOpen(false);
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const onConfirmStatement = () => {
    setShowStatementWarning(false);
    openConfirmation();
  };

  const closeStatementWarning = () => setShowStatementWarning(false);

  const onSubmit = () => {
    setShowStatementWarning(true);
    return api.post('/customer/otp/generate');
  };

  const onResend = () => api.post('/customer/otp/generate');

  const onFormSubmit = (values: IFormValues, otp: string) => {
    const requestBody = {
      accountNumber: values.sourceAccount,
      startDate: getParsedDate(values.startDate),
      endDate: getParsedDate(values.endDate),
      branch: values.branch,
      otp,
    };
    return api.post('account/officialStatement', requestBody);
  };
  const onOtpSubmit = (values: IFormValues) => (otp: string | undefined, form: FormTypeOTP | undefined) =>
    api
      .post(`customer/otp/verify/${otp || ''}/false/${String(sessionStorage.getItem('userName'))}`)
      .then(() => {
        onFormSubmit(values, String(otp))
          .then((data: IApiResponse) => {
            setSuccessMsg(data.description);
            setSubmitStatus(AxiosStatus.SUCCESS);
          })
          .catch((error: IApiResponse) => {
            setErrorMsg(error.description);
            setSubmitStatus(AxiosStatus.FAILURE);
          })
          .finally(() => setOTPModalOpen(false));
      })
      .catch((error: IOTPError) => {
        setOTPError(error);
        if (error.failureCount === 0 && form) {
          form.initialize({ digit1: '', digit2: '', digit3: '', digit4: '', digit5: '' });
        }
        return { [FORM_ERROR]: error.description || NETWORK_REQUIRED };
      });

  const onChangeForm = ({ dirty }: FormState<IFormValues>) => {
    if (dirty) {
      handleChangeForm();
    }
  };

  return (
    <>
      {loadingData && <Loader />}
      {!loadingData && (
        <Form
          onSubmit={onSubmit}
          validate={validate}
          mutators={{ changeFieldValue }}
          initialValues={initialValues}
          render={({ handleSubmit, values, form, hasValidationErrors }) => {
            mainForm = form;

            return (
              <form onSubmit={handleSubmit}>
                <>
                  <FieldsLeveler label="Official Statement Details">
                    {renderBankStatementFields(
                      accounts,
                      branches,
                      submitting,
                      handleChangeStartDate,
                      handleChangeEndDate,
                    )}
                  </FieldsLeveler>
                  <SubmitLeveler>
                    <Button type="submit" fullWidth disabled={submitting || hasValidationErrors}>
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Request Statement'}
                    </Button>
                  </SubmitLeveler>
                  <WarningModal
                    open={showStatementWarning}
                    submitName="Proceed"
                    onSubmit={onConfirmStatement}
                    onClose={closeStatementWarning}
                  >
                    Are you sure you want to submit bank statement request?
                  </WarningModal>
                  <OTPVerificationModal
                    open={isOTPModalOpen}
                    onClose={closeConfirmation}
                    onSubmit={onOtpSubmit(values)}
                    onResend={onResend}
                    OTPModalError={OTPError}
                  />
                  <StatusModal
                    open={showStatusModal}
                    status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
                    onClose={onCloseStatusModal}
                    onRetry={onCloseStatusModal}
                    onRetryText="Close"
                  >
                    {submitStatus === AxiosStatus.SUCCESS ? successMsg : errorMsg}
                  </StatusModal>
                </>
                <FormSpy subscription={{ dirty: true }} onChange={onChangeForm} />
              </form>
            );
          }}
        />
      )}
    </>
  );
};

export default OfficialStatement;
